import React, { useMemo, useState } from "react";

/**
 * OverviewTab1
 * Matches the screenshot layout:
 * Left column: Diagrams, Highlights, Engineering decisions, Receipts
 * Right column: Stack summary, Production readiness, Security, Observability, Cost
 */

const DiagramTabs = [
  { key: "architecture", label: "Architecture" },
  { key: "dataflow", label: "Data flow" },
  { key: "deployment", label: "Deployment" },
];

function Card({ title, hint, children, className = "" }) {
  return (
    <section className={`pdCard ${className}`}>
      <header className="pdCard__header">
        <div className="pdCard__titleRow">
          <h3 className="pdCard__title">{title}</h3>
        </div>
        {hint ? <p className="pdCard__hint">{hint}</p> : null}
      </header>
      <div className="pdCard__body">{children}</div>
    </section>
  );
}

function MiniKVs({ items = [] }) {
  return (
    <div className="pdKV">
      {items.map(({ k, v }) => (
        <React.Fragment key={k}>
          <div className="pdKV__k">{k}</div>
          <div className="pdKV__v">{v}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

function Bullets({ items = [] }) {
  return (
    <ul className="pdBullets">
      {items.map((t, i) => (
        <li key={`${t}-${i}`}>{t}</li>
      ))}
    </ul>
  );
}

function DecisionList({ items = [] }) {
  return (
    <div className="pdDecisionList">
      {items.map((d, i) => (
        <div key={`${d.title}-${i}`} className="pdDecision">
          <div className="pdDecision__title">{d.title}</div>
          <div className="pdDecision__desc">{d.description}</div>
        </div>
      ))}
    </div>
  );
}

function ReceiptGrid({ items = [] }) {
  return (
    <div className="pdReceipts">
      {items.map((r, i) => (
        <div key={`${r.title}-${i}`} className="pdReceipt">
          <div className="pdReceipt__title">{r.title}</div>
          <div className="pdReceipt__desc">{r.description}</div>
          <div className="pdReceipt__drop">
            <div className="pdReceipt__dropInner" />
          </div>
        </div>
      ))}
    </div>
  );
}


export default function OverviewTab1({ project }) {

  // If your data has real content, it will show. Otherwise, these defaults match the screenshot.
  const data = useMemo(() => {
    const links = project?.links || {};

    return {
      overview : project.overview,
      features: project.features,
      stackSummary: project?.stackSummary || [
        { k: "Compute", v: "AWS Lambda" },
        { k: "Ingress", v: "API Gateway" },
        { k: "Data", v: "DynamoDB" },
        { k: "Async", v: "SQS + DLQ" },
        { k: "IaC", v: "CDK" },
        { k: "CI/CD", v: "GitHub Actions (OIDC)" },
        { k: "Observability", v: "CloudWatch + X-Ray" },
        { k: "Security", v: "Least-privilege IAM + Secrets Manager" },
      ],
      highlights: project?.highlights || [
        "PR checks: unit tests, lint, IaC synth, security scan",
        "Deployments: dev → staging → prod with approvals",
        "Resilience: retries, DLQ, idempotency keys",
        "Cost controls: DynamoDB TTL, Lambda reserved concurrency",
      ],
      productionReadiness: project?.productionReadiness || [
        "Health checks + alarms",
        "SQS DLQ with redrive policy",
        "Timeouts and retries tuned",
        "Tracing enabled (X-Ray)",
        "Backups/retention documented",
      ],
      security: project?.securityNotes || [
        "Least privilege IAM, scoped roles per service",
        "Secrets stored in managed secret store",
        "Auditability: logs, traces, deploy history",
      ],
      observability: project?.observability || [
      
      ],
      cost: project?.costNotes || [
      ],
      decisions: project?.engineeringDecisions || [
        {
          title: "DynamoDB over RDS",
          description:
            "Chosen for predictable scaling and simple access patterns (orders by customer, status). Transactions avoided by using idempotency keys and event-driven updates.",
        },
        {
          title: "Async via SQS",
          description:
            "Long-running side effects (email, analytics) moved off the request path to keep p95 low and isolate failures.",
        },
      ],
      // receipts: project?.receipts || [
      //   { title: "CI run (tests + deploy)", description: "Add a screenshot here (redact secrets)." },
      //   { title: "CloudWatch dashboard", description: "Add a screenshot here (redact secrets)." },
      //   { title: "Terraform/CDK diff", description: "Add a short snippet or excerpt here." },
      //   { title: "Example structured logs", description: "Add a short snippet or excerpt here." },
      // ],
      codeUrl: links.repo || links.pipeline || "",
    };
  }, [project]);

  return (
    <div className="pdLayout">
      <div className="pdMain">

         <Card title="Overview" hint="">
          <DecisionList items={data.overview} />
        </Card>
        <Card title="Features">
          <Bullets items={data.features}/>
        </Card>
        <Card title="Highlights" hint="What makes this project production-ready">
          <Bullets items={data.highlights} />
        </Card>

        <Card title="Engineering decisions" hint="Tradeoffs, not just tooling">
          <DecisionList items={data.decisions} />
        </Card>

        <Card title="Receipts" hint="Proof: pipeline runs, dashboards, diffs, logs">
          <ReceiptGrid items={data.receipts} />
        </Card>
      </div>

      <aside className="pdSide">
        <Card title="Stack summary" hint="Quick scan for reviewers" className="pdCard--sticky">
          <MiniKVs items={data.stackSummary} />
          <div className="pdMiniActions">
            <button type="button" className="pdMiniBtn">
              Copy
            </button>
            {data.codeUrl ? (
              <a className="pdMiniBtn pdMiniBtn--link" href={data.codeUrl} target="_blank" rel="noreferrer">
                View code
                <span className="pdMiniBtn__ext" aria-hidden="true">
                  ↗
                </span>
              </a>
            ) : (
              <button type="button" className="pdMiniBtn" disabled>
                View code
              </button>
            )}
          </div>
        </Card>

        <Card title="Production readiness" >
          <Bullets items={data.productionReadiness} />
        </Card>

        <Card title="Security" hint="To reduce blast radius">
          <Bullets items={data.security} />
        </Card>

        <Card title="Observability" hint="How to know it works">
          <Bullets items={data.observability} />
        </Card>

        <Card title="Cost" hint="">
          <Bullets items={data.cost} />
        </Card>
      </aside>
    </div>
  );
}
